import {
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/outline";
import { Select, Option, Chip } from "@material-tailwind/react";
import React from "react";
import { ArrowDownIcon } from "@heroicons/react/24/solid";
import {
  Card,
  CardHeader,
  Input,
  Typography,
  Button,
  CardBody,
  CardFooter,
  IconButton,
} from "@material-tailwind/react";
import { useEffect, useState } from "react";
import { getEvidenceCollectionData } from "../../helperFunctions/apiFunction";
import { useAuthUserToken } from "../../hooks/useAuthUserToken";

import ExcelJS from "exceljs";
import { useParams } from "react-router-dom";
import TableSkeleton from "../../components/Loaders/TableSkeleton";
import TableRowWithShowMore from "../../components/TableRowWithShowMore";

const TABLE_HEAD = [
  "Cloud",
  "Service",
  "Domain",
  "Name",
  "Description",
  "Recommendation",
  "Reference",
];

export default function EvidencePageCollection() {
  const { completeAuthData: authData, refreshToken, rbac } = useAuthUserToken();
  const [templateData, setTemplateData] = useState([]);
  const { Collection } = useParams();
  const [isLoading, setIsLoading] = useState(null);
  const [searchBy, setSearchBy] = useState("");
  const [sortBy, setSortBy] = useState("asc");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [tableHead, setTableHead] = useState([
    "Title",
    "Owner",
    "Compliance",
    "Status",
  ]);
  const [title, setTitle] = useState("");
  const [subTitle, setSubTitle] = useState("");

  useEffect(() => {
    setIsLoading(null);

    const collections = {
      documentdata: {
        tableHead: ["Title", "Owner", "Compliance", "Status"],
        title: "List of Policy Documents",
        subTitle: "View all organization information security policy documents",
      },
      scanresultdata: {
        tableHead: [
          "Type",
          "Title",
          "Description",
          "Category",
          "Region",
          "Resource",
          "Status",
          "Output",
        ],
        title: "List of CSPM Scan Findings",
        subTitle:
          "Detailed Report on Identified Security Issues and Recommendations",
      },
      integrationdata: {
        tableHead: ["Cloud", "Title", "Description", "Configuration", "Status"],
        title: "List of Integrations",
        subTitle: "View all monitored cloud accounts",
      },
      vendordata: {
        tableHead: ["Name", "Type", "Status", "Compliance"],
        title: "List of Vendors",
        subTitle:
          "View all vendors and their compliance against compliance requirements",
      },
      peopledata: {
        tableHead: [
          "Employee Status",
          "Employee Department",
          "Employee Designation",
          "Training Completion Status",
          "Training Assigned On",
          "Training Completed On",
        ],
        title: "List of Employees",
        subTitle:
          "View all employees and compliance of assigned trainings",
      },
    };

    if (collections[Collection]) {
      const { tableHead, title, subTitle } = collections[Collection];
      setTableHead(tableHead);
      setTitle(title);
      setSubTitle(subTitle);
    } else {
      setTableHead([]);
      setTitle("");
      setSubTitle("");
    }

    getEvidenceCollectionData(
      refreshToken,
      Collection,
      searchBy,
      sortBy,
      limit,
      page
    ).then((data) => {
      setTotalPage(data?.totalPages);
      setTemplateData(data?.data);
      setIsLoading(false);
    });
  }, [page, limit, sortBy, searchBy, Collection]);

  const exportToExcel = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("TrainingManagement Data");

    const headers = [
      "S.No.",
      "Scan Type",
      "Test Title",
      "Test Description",
      "Resource Category",
      "Region",
      "Resource",
      "Test Status",
      "Test Output",
    ];

    worksheet.addRow(headers);

    templateData.forEach((row, index) => {
      worksheet.addRow([
        index + 1,
        row.scanType,
        row.testTitle,
        row.testDescription,
        row.cloudAssetCategory,
        row.cloudAssetRegion,
        row.cloudAssetId,
        row.testStatus,
        row.testOutput,
      ]);
    });

    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "trainingManagement_data.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <div className="app-main__inner !pl-20 ">
      <Card
        className="h-full w-full rounded-3xl shadow-xl"
        style={{ backgroundImage: `url('/assets/images/beams.jpg')` }}
      >
        <CardHeader
          floated={false}
          shadow={false}
          className=" bg-transparent rounded-none !overflow-visible"
        >
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between md:gap-8">
            <div>
              <Typography variant="h5" color="blue-gray">
                {title}
              </Typography>
              <Typography color="gray" className="mt-1 font-normal">
                {subTitle}
              </Typography>
            </div>
            <div className="flex flex-row md:flex-row items-center justify-between md:items-center gap-2 md:gap-8 mt-4 md:mt-0">
              <Button
                variant="outlined"
                size="sm"
                onClick={exportToExcel}
                loading={isLoading === null ? true : false}
              >
                <ArrowDownIcon strokeWidth={2} className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
              <Select
                color="blue"
                label="Select Limit"
                className="!border-t-0 !border-[#b0bec5]"
                onChange={(event) => {
                  setLimit(event);
                  setIsLoading(null);
                }}
                value={limit}
              >
                <Option value={10}>10</Option>
                <Option value={20}>20</Option>
                <Option value={30}>30</Option>
                <Option value={40}>40</Option>
                <Option value={50}>50</Option>
              </Select>
              <Select
                color="purple"
                label="Sort By"
                className="!border-t-0 !border-[#b0bec5]"
                value={sortBy}
                onChange={(event) => {
                  setSortBy(event);
                  setIsLoading(null);
                }}
              >
                <Option value="asc">Ascending</Option>
                <Option value="desc">Descending</Option>
              </Select>
            </div>
            <div className="w-full md:w-auto">
              <Input
                label="Search"
                value={searchBy}
                className="focus:!border-black focus:!border-2 focus:!border-t-0"
                onChange={(e) => setSearchBy(e.target.value)}
                icon={<MagnifyingGlassIcon className="h-5 w-5" />}
              />
            </div>
          </div>
        </CardHeader>
        <CardBody className="overflow-scroll px-3 p-0">
          <table className="mt-4 w-full min-w-max table-auto text-left">
            <thead>
              <tr>
                {tableHead.map((head, index) => (
                  <th
                    key={head}
                    className="cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-4 transition-colors hover:bg-blue-gray-50 w-32"
                  >
                    <Typography
                      variant="small"
                      color="blue-gray"
                      className="flex items-center justify-between gap-2 font-normal leading-none opacity-70"
                    >
                      {head}{" "}
                      {index !== tableHead.length - 1 && (
                        <ChevronUpDownIcon
                          strokeWidth={2}
                          className="h-4 w-4"
                        />
                      )}
                    </Typography>
                  </th>
                ))}
              </tr>
            </thead>
            {isLoading === null ? (
              <>
                <tbody>
                  <tr>
                    <td colSpan={tableHead.length}>
                      <TableSkeleton />
                    </td>
                  </tr>
                </tbody>
              </>
            ) : (
              <>
                <tbody>
                  {isLoading === false && templateData?.length > 0 ? (
                    templateData.map((item, index) => {
                      if (!item) return null;
                      let cloudAssetCategory,
                        scanType,
                        testDescription,
                        testStatus,
                        testTitle,
                        cloudAssetId,
                        cloudAssetRegion,
                        testOutput,
                        integrationProvider,
                        integrationTitle,
                        integrationDescription,
                        integrationConfig,
                        integrationStatus,
                        docTitle,
                        owner,
                        complianceStatus,
                        status,
                        vendorLegalName,
                        vendorType,
                        vendorStatus,
                        vendorComplianceStatus,
                        empStatus,
                        empDepartment,
                        empDesignation,
                        trainingAssignedOn,
                        trainingCompletedOn,
                        _id;

                      if (Collection === "scanresultdata") {
                        ({
                          cloudAssetCategory,
                          scanType,
                          testDescription,
                          testStatus,
                          cloudAssetId,
                          testTitle,
                          testOutput,
                          cloudAssetRegion,
                          _id,
                        } = item);
                      } else if (Collection === "integrationdata") {
                        ({
                          integrationProvider,
                          integrationTitle,
                          integrationDescription,
                          integrationConfig,
                          integrationStatus,
                          _id,
                        } = item);
                      } else if (Collection === "documentdata") {
                        ({ docTitle, owner, complianceStatus, status, _id } =
                          item);
                      } else if (Collection === "vendordata") {
                        ({
                          vendorLegalName,
                          vendorType,
                          vendorStatus,
                          vendorComplianceStatus,
                          _id,
                        } = item);
                      } else if (Collection === "peopledata") {
                        ({
                          empStatus,
                          empDepartment,
                          empDesignation,
                          trainingAssignedOn,
                          trainingCompletedOn,
                          _id,
                        } = item);
                      }
                      const isLast = index === templateData.length - 1;
                      const classes = isLast
                        ? "p-4"
                        : "p-4 border-b border-blue-gray-50";

                      return (
                        (Collection === "documentdata" && (
                          <tr key={`${_id}-${index}`}>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {docTitle}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {owner}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {complianceStatus}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <div className="w-max">
                                <Chip
                                  value={status}
                                  variant="ghost"
                                  size="md"
                                  // color={}
                                />
                              </div>
                            </td>
                          </tr>
                        )) ||
                        (Collection === "integrationdata" && (
                          <tr key={`${_id}-${index}`}>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {integrationProvider}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {integrationTitle}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {integrationDescription}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {integrationConfig}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Chip
                                variant="ghost"
                                size="sm"
                                value={
                                  integrationStatus === "Active"
                                    ? "Active"
                                    : "Deactive"
                                }
                                color={
                                  integrationStatus === "Active"
                                    ? "green"
                                    : "red"
                                }
                              />
                            </td>
                          </tr>
                        )) ||
                        (Collection === "scanresultdata" && (
                          <tr key={`${_id}-${index}`}>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {scanType}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {testTitle}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {testDescription.length > 40 ? (
                                  <TableRowWithShowMore
                                    data={testDescription}
                                  />
                                ) : (
                                  testDescription
                                )}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {cloudAssetCategory}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {cloudAssetRegion}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {cloudAssetId.length > 40 ? (
                                  <TableRowWithShowMore data={cloudAssetId} />
                                ) : (
                                  cloudAssetId
                                )}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <div className="w-max">
                                <Chip
                                  value={testStatus}
                                  variant="ghost"
                                  size="md"
                                  color={
                                    testStatus === "OK"
                                      ? "green"
                                      : testStatus === "FAIL"
                                      ? "red"
                                      : "gray"
                                  }
                                />
                              </div>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {testOutput.length > 40 ? (
                                  <TableRowWithShowMore data={testOutput} />
                                ) : (
                                  testOutput
                                )}
                              </Typography>
                            </td>
                          </tr>
                        )) ||
                        (Collection === "vendordata" && (
                          <tr key={`${_id}-${index}`}>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {vendorLegalName}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {vendorType}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <div className="w-max">
                                <Chip
                                  variant="ghost"
                                  size="sm"
                                  value={vendorStatus}
                                  color={
                                    vendorStatus === "Employed"
                                      ? "green"
                                      : "red"
                                  }
                                />
                              </div>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {vendorComplianceStatus}
                              </Typography>
                            </td>
                          </tr>
                        )) ||
                        (Collection === "peopledata" && (
                          <tr key={`${_id}-${index}`}>
                            <td className={classes}>
                              <div className="w-max">
                                <Chip
                                  variant="ghost"
                                  size="sm"
                                  value={empStatus}
                                  color={
                                    empStatus === "Employed" ? "green" : "red"
                                  }
                                />
                              </div>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {empDepartment}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {empDesignation}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {trainingAssignedOn}
                              </Typography>
                            </td>

                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {trainingCompletedOn}
                              </Typography>
                            </td>
                          </tr>
                        ))
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8">
                        <div className="flex flex-col items-center w-full">
                          <img
                            quality={100}
                            src="/assets/images/9276430.jpg"
                            className="w-[350px] mix-blend-darken"
                            alt="no-data-found"
                          />
                          <p className="text-[20px] font-medium">
                            No Data Found!
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </>
            )}
          </table>
        </CardBody>
        {isLoading === null ? null : (
          <>
            <CardFooter className="flex items-center justify-between border-t border-blue-gray-50 p-4">
              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setPage(page - 1);
                  setIsLoading(null);
                }}
                disabled={totalPage > 1 ? false : true}
              >
                Previous
              </Button>
              <div className="flex items-center gap-2">
                {Array.from({ length: totalPage }, (_, index) => (
                  <IconButton
                    key={index + 1}
                    variant={index + 1 === page ? "outlined" : "text"}
                    size="sm"
                    onClick={() => {
                      setPage(index + 1);
                      setIsLoading(null);
                    }}
                    disabled={totalPage > 1 ? false : true}
                  >
                    {index + 1}
                  </IconButton>
                ))
                  .reduce((acc, button, index, array) => {
                    if (
                      index < 3 ||
                      index >= totalPage - 3 ||
                      (index >= Math.max(page - 1, 3) &&
                        index <= Math.min(page + 1, totalPage - 4))
                    ) {
                      acc.push(button);
                    } else if (acc[acc.length - 1] !== "ellipsis") {
                      acc.push("ellipsis");
                    }
                    return acc;
                  }, [])
                  .map((element, index, array) => {
                    return element === "ellipsis" ? (
                      <IconButton
                        key={`ellipsis-${index}`}
                        variant="text"
                        size="sm"
                        disabled
                      >
                        ...
                      </IconButton>
                    ) : index > 0 && array[index - 1] === "ellipsis" ? (
                      <React.Fragment key={`fragment-${index}`}>
                        {element}
                      </React.Fragment>
                    ) : (
                      element
                    );
                  })}
              </div>

              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setPage(page + 1);
                  setIsLoading(null);
                }}
                disabled={totalPage > 1 ? false : true}
              >
                Next
              </Button>
            </CardFooter>
          </>
        )}
      </Card>
    </div>
  );
}
